/**
 * Human-readable descriptions for permissions
 * Maps permission IDs (resource.action) to friendly descriptions
 */
export const permissionDescriptions: Record<string, string> = {
    // Members
    'members.create': 'Create new members and volunteers',
    'members.read': 'View members and volunteer information',
    'members.update': 'Edit member details and update merchandise distribution status',
    'members.delete': 'Remove members from the system',

    // Transactions (Financial)
    'transactions.create': 'Record new financial transactions',
    'transactions.read': 'View financial transaction history and reports',
    'transactions.update': 'Modify transaction details and correct entries',
    'transactions.delete': 'Delete transaction records (use with caution)',

    // Donations (Campaigns)
    'donations.create': 'Create new donation campaigns',
    'donations.read': 'View donation campaigns and contributions',
    'donations.update': 'Edit campaign details and approve/reject donations',
    'donations.delete': 'Remove donation campaigns',

    // Events
    'events.create': 'Create new events',
    'events.read': 'View events and RSVP lists',
    'events.update': 'Edit event details and manage RSVPs',
    'events.delete': 'Cancel and remove events',

    // Activities
    'activities.create': 'Create new community activities',
    'activities.read': 'View community activities',
    'activities.update': 'Edit activity information',
    'activities.delete': 'Remove activities',

    // Partners
    'partners.create': 'Add new partner organizations',
    'partners.read': 'View partner information',
    'partners.update': 'Edit partner details',
    'partners.delete': 'Remove partners',

    // Contacts
    'contacts.create': 'Submit contact form messages',
    'contacts.read': 'View contact form submissions',
    'contacts.update': 'Edit contact message details and notes',
    'contacts.delete': 'Delete contact messages',

    // Posters
    'posters.create': 'Upload new posters',
    'posters.read': 'View poster gallery',
    'posters.update': 'Edit posters and toggle active status',
    'posters.delete': 'Remove posters',

    // Blogs
    'blogs.create': 'Write new blog posts',
    'blogs.read': 'View blog posts',
    'blogs.update': 'Edit existing blog posts',
    'blogs.delete': 'Delete blog posts',
    'blogs.publish': 'Publish and unpublish blog posts',

    // Media
    'media.create': 'Upload new media files (images, videos, documents)',
    'media.read': 'View media gallery',
    'media.update': 'Edit media files and metadata',
    'media.delete': 'Remove media files',

    // Products (Store)
    'products.create': 'Add new products to the store',
    'products.read': 'View product catalog',
    'products.update': 'Edit product details and manage inventory',
    'products.delete': 'Remove products from the store',

    // Orders (Store)
    'orders.create': 'Create new orders',
    'orders.read': 'View order history and details',
    'orders.update': 'Update order status and process orders',
    'orders.delete': 'Cancel and delete orders',

    // Roles (RBAC)
    'roles.create': 'Create new user roles',
    'roles.read': 'View roles and their permissions',
    'roles.update': 'Edit role details and manage role permissions',
    'roles.delete': 'Remove user roles',

    // Users (RBAC)
    'users.read': 'View user list and details',
    'users.manage_roles': 'Assign and remove roles from users',

    // Audit Logs
    'audit_logs.read': 'View system audit logs and user activity',
};

/**
 * Get a human-readable description for a permission
 * @param permission Permission ID in format "resource.action"
 * @returns Friendly description or the permission ID if not found
 */
export function getPermissionDescription(permission: string): string {
    return permissionDescriptions[permission] || permission;
}

/**
 * Parse a permission string into resource and action
 * @param permission Permission ID in format "resource.action"
 * @returns Object with resource and action, or null if invalid format
 */
export function parsePermission(permission: string): { resource: string; action: string } | null {
    const parts = permission.split('.');
    if (parts.length !== 2) return null;

    return {
        resource: parts[0],
        action: parts[1],
    };
}
